import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Download, FileWarning } from "lucide-react";

// Version-aware CDN worker — reliable under CRA (no import.meta.url bundling needed).
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// In-app PDF viewer. `blob` is a Blob; renders every page as a canvas so it works
// consistently inside mobile / PWA in-app browsers (no native PDF plugin needed).
export function PdfViewer({ open, onClose, blob, title = "Document", onDownload }) {
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState(false);
  const [width, setWidth] = useState(0);
  const bodyRef = useRef(null);

  // Memoize the file prop (react-pdf uses === to detect changes).
  const file = useMemo(() => (blob ? { url: URL.createObjectURL(blob) } : null), [blob]);
  useEffect(() => () => { if (file?.url) URL.revokeObjectURL(file.url); }, [file]);

  // Reliable width via ResizeObserver: keeps pages fit-to-container as the dialog
  // animates in and on orientation changes (fixes tiny/overflowing pages on mobile).
  useEffect(() => {
    if (!open) { setNumPages(0); setError(false); return; }
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => setWidth(Math.min(el.clientWidth - 16, 900));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="flex h-[92vh] max-h-[92vh] w-[calc(100%-1rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:h-[85vh]" data-testid="pdf-viewer-dialog">
        <DialogHeader className="flex shrink-0 flex-row items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
          <DialogTitle className="min-w-0 flex-1 truncate pr-6 text-sm">{title}</DialogTitle>
          {onDownload && (
            <Button size="sm" variant="outline" onClick={onDownload} data-testid="pdf-viewer-download" className="mr-6 h-8 shrink-0 gap-1.5 text-xs">
              <Download className="h-3.5 w-3.5" />Download
            </Button>
          )}
        </DialogHeader>
        <div ref={bodyRef} className="min-h-0 flex-1 overflow-auto bg-slate-100 px-2 py-3" data-testid="pdf-viewer-body">
          {error ? (
            <div className="flex flex-col items-center gap-2 py-16 text-center text-sm text-muted-foreground">
              <FileWarning className="h-6 w-6" />
              Couldn't display this PDF. Use Download instead.
            </div>
          ) : (
            <Document
              file={file}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              onLoadError={() => setError(true)}
              loading={<div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading PDF…</div>}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={i}
                  pageNumber={i + 1}
                  width={width || undefined}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="mx-auto mb-3 shadow-sm"
                />
              ))}
            </Document>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
